import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../lib/api';
import { confirmDialog } from '../../utils/dialogs';

export default function UserManagementModal({ onClose }) {
    const [activeTab, setActiveTab] = useState("users"); // "users" or "codes"
    const [users, setUsers] = useState([]);
    const [registrationCodes, setRegistrationCodes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [editingUser, setEditingUser] = useState(null);

    useEffect(() => {
        // Fetch real data from backend
        const fetchData = async () => {
            try {
                setLoading(true);
                setError("");

                // Fetch registration codes
                const codesResponse = await fetch(`${API_BASE}/auth/codes`, {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('go2cdl_access_token')}`,
                    },
                });

                if (codesResponse.ok) {
                    const codesData = await codesResponse.json();
                    setRegistrationCodes(codesData || []);
                }

                // Fetch real users from backend
                const usersResponse = await fetch(`${API_BASE}/api/users`, {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('go2cdl_access_token')}`,
                    },
                });

                console.log('Users response status:', usersResponse.status);

                if (usersResponse.ok) {
                    const usersData = await usersResponse.json();
                    console.log('Users data from API_BASE:', usersData.users);
                    setUsers(usersData.users || []);
                } else {
                    const errorData = await usersResponse.json();
                    console.error('Failed to fetch users:', errorData);
                    setError("Failed to fetch users from server");
                }

            } catch (error) {
                console.error("Error fetching data:", error);
                setError("Database connection issue. Please check server configuration.");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const generateCode = async (role) => {
        try {
            setLoading(true);
            setError("");

            const response = await fetch(`${API_BASE}/auth/generate-code`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem('go2cdl_access_token')}`,
                },
                body: JSON.stringify({ role }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || "Failed to generate code");
            }

            const data = await response.json();
            const newCode = data.code;
            const generatedRole = data.role || 'STUDENT';

            // Preserve the database values returned by the server so deletion and expiry stay accurate.
            const codeObject = {
                id: data.id,
                code: newCode,
                role: generatedRole,
                used: false,
                expiresAt: data.expiresAt,
                createdAt: data.createdAt,
            };

            setRegistrationCodes(prev => [codeObject, ...prev]);

            // Copy to clipboard
            navigator.clipboard.writeText(newCode);
            alert(`Registration code generated and copied to clipboard: ${newCode}`);
        } catch (error) {
            console.error("Error generating code:", error);
            setError(error.message || "Failed to generate code");
        } finally {
            setLoading(false);
        }
    };

    const deleteCode = async (codeId) => {
        if (confirmDialog("Are you sure you want to delete this registration code?")) {
            try {
                setLoading(true);
                setError("");

                const response = await fetch(`${API_BASE}/auth/codes/${codeId}`, {
                    method: "DELETE",
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('go2cdl_access_token')}`,
                    },
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || "Failed to delete code");
                }

                // Remove from local state
                setRegistrationCodes(prev => prev.filter(code => code.id !== codeId));
                alert("Registration code deleted successfully");
            } catch (error) {
                console.error("Error deleting code:", error);
                setError(error.message || "Failed to delete code");
            } finally {
                setLoading(false);
            }
        }
    };

    const toggleUserStatus = async (userId) => {
        try {
            setLoading(true);
            setError("");

            const currentUser = users.find(u => u.id === userId);
            const newStatus = currentUser.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";

            const response = await fetch(`${API_BASE}/auth/users/${userId}/status`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem('go2cdl_access_token')}`,
                },
                body: JSON.stringify({ status: newStatus }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || "Failed to update user status");
            }

            // Update local state
            setUsers(prev => prev.map(user =>
                user.id === userId
                    ? { ...user, status: newStatus }
                    : user
            ));

            alert(`User status updated to ${newStatus.toLowerCase()}`);
        } catch (error) {
            console.error("Error updating user status:", error);
            if (error.message.includes("Failed to fetch")) {
                setError("Database connection issue. Please check server configuration.");
            } else {
                setError(error.message || "Failed to update user status");
            }
        } finally {
            setLoading(false);
        }
    };

    const deleteUser = async (userId) => {
        if (!confirmDialog("Are you sure you want to delete this user? This action cannot be undone.")) {
            return;
        }

        try {
            setLoading(true);
            setError("");

            const response = await fetch(`${API_BASE}/auth/users/${userId}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('go2cdl_access_token')}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || "Failed to delete user");
            }

            // Remove user from local state
            setUsers(prev => prev.filter(user => user.id !== userId));

            alert("User deleted successfully");
        } catch (error) {
            console.error("Error deleting user:", error);
            setError(error.message || "Failed to delete user");
        } finally {
            setLoading(false);
        }
    };

    const updateUserPhone = async (userId) => {
        try {
            setLoading(true);
            setError("");

            const response = await fetch(`${API_BASE}/auth/users/${userId}/phone/prompt`, {
                method: 'PATCH',
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('go2cdl_access_token')}`,
                }
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || 'Failed to trigger phone prompt');
            }

            setUsers(prev => prev.map(user =>
                user.id === userId ? { ...user, phonePromptRequired: true } : user
            ));
            alert('The student will be prompted to provide their phone number on their dashboard.');
        } catch (error) {
            console.error('Failed to trigger phone prompt:', error);
            setError(error.message || 'Failed to trigger phone prompt');
        } finally {
            setLoading(false);
        }
    };

    const updateUserNames = async (userId, firstName, lastName) => {
        try {
            setLoading(true);
            setError("");

            console.log('Updating names for user:', userId, 'with names:', firstName, lastName);

            const response = await fetch(`${API_BASE}/auth/users/${userId}/names`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem('go2cdl_access_token')}`,
                },
                body: JSON.stringify({ firstName, lastName }),
            });

            console.log('Response status:', response.status);

            if (!response.ok) {
                const errorData = await response.json();
                console.error('Server error:', errorData);
                throw new Error(errorData.error || "Failed to update user names");
            }

            const result = await response.json();
            console.log('Success response:', result);

            // Update user in local state
            setUsers(prev => prev.map(user =>
                user.id === userId
                    ? { ...user, firstName, lastName }
                    : user
            ));

            setEditingUser(null);
            alert("User names updated successfully");
        } catch (error) {
            console.error("Error updating user names:", error);
            setError(error.message || "Failed to update user names");
        } finally {
            setLoading(false);
        }
    };



    return (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center p-4 z-50 pt-20">
            <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-6 border-b">
                    <h2 className="text-2xl font-bold">User Management</h2>
                    <button type="button" onClick={onClose} className="inline-flex shrink-0 items-center gap-2 rounded-2xl bg-slate-800 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-900" aria-label="Back to dashboard"><span className="text-lg leading-none" aria-hidden="true">←</span> Back</button>
                </div>

                <div className="p-6">
                    {/* Error Display */}
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                            {error}
                        </div>
                    )}

                    {/* Tab Navigation */}
                    <div className="flex border-b mb-6">
                        <button
                            onClick={() => setActiveTab("users")}
                            className={`px-4 py-2 font-medium transition-colors ${activeTab === "users"
                                ? "border-b-2 border-blue-500 text-blue-600"
                                : "text-gray-500 hover:text-gray-700"
                                }`}
                        >
                            Users ({users.length})
                        </button>
                        <button
                            onClick={() => setActiveTab("codes")}
                            className={`px-4 py-2 font-medium transition-colors ${activeTab === "codes"
                                ? "border-b-2 border-blue-500 text-blue-600"
                                : "text-gray-500 hover:text-gray-700"
                                }`}
                        >
                            Registration Codes ({registrationCodes.length})
                        </button>
                    </div>

                    {/* Users Tab */}
                    {activeTab === "users" && (
                        <div>
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-semibold">User Accounts</h3>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full border-collapse border border-gray-200">
                                    <thead>
                                        <tr className="bg-gray-50">
                                            <th className="border border-gray-200 px-4 py-2 text-left">ID</th>
                                            <th className="border border-gray-200 px-4 py-2 text-left">First Name</th>
                                            <th className="border border-gray-200 px-4 py-2 text-left">Last Name</th>
                                            <th className="border border-gray-200 px-4 py-2 text-left">Phone</th>
                                            <th className="border border-gray-200 px-4 py-2 text-left">Email</th>
                                            <th className="border border-gray-200 px-4 py-2 text-left">Role</th>
                                            <th className="border border-gray-200 px-4 py-2 text-left">Status</th>
                                            <th className="border border-gray-200 px-4 py-2 text-left">Created</th>
                                            <th className="border border-gray-200 px-4 py-2 text-left">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {users.map(user => (
                                            <tr key={user.id} className="hover:bg-gray-50">
                                                <td className="border border-gray-200 px-4 py-2">{user.id}</td>
                                                <td className="border border-gray-200 px-4 py-2">
                                                    {user.firstName ? user.firstName : <span className="text-gray-400 italic">Not set</span>}
                                                </td>
                                                <td className="border border-gray-200 px-4 py-2">
                                                    {user.lastName ? user.lastName : <span className="text-gray-400 italic">Not set</span>}
                                                </td>
                                                <td className="border border-gray-200 px-4 py-2">
                                                    {user.phone ? (
                                                        user.phone
                                                    ) : (
                                                        <button
                                                            onClick={() => updateUserPhone(user.id)}
                                                            className="text-xs text-blue-600 underline"
                                                        >
                                                            Missing
                                                        </button>
                                                    )}
                                                </td>
                                                <td className="border border-gray-200 px-4 py-2">{user.email}</td>
                                                <td className="border border-gray-200 px-4 py-2">
                                                    <span className={`px-2 py-1 rounded-full text-xs ${user.role === "ADMIN" ? "bg-red-100 text-red-800" :
                                                        user.role === "INSTRUCTOR" ? "bg-blue-100 text-blue-800" :
                                                            "bg-green-100 text-green-800"
                                                        }`}>
                                                        {user.role.toLowerCase()}
                                                    </span>
                                                </td>
                                                <td className="border border-gray-200 px-4 py-2">
                                                    <span className={`px-2 py-1 rounded-full text-xs ${user.status === "ACTIVE" ? "bg-green-100 text-green-800" :
                                                        user.status === "INACTIVE" ? "bg-red-100 text-red-800" :
                                                            "bg-yellow-100 text-yellow-800"
                                                        }`}>
                                                        {user.status.toLowerCase()}
                                                    </span>
                                                </td>
                                                <td className="border border-gray-200 px-4 py-2">
                                                    {new Date(user.createdAt).toLocaleDateString()}
                                                </td>
                                                <td className="border border-gray-200 px-4 py-2">
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => toggleUserStatus(user.id)}
                                                            className={`px-3 py-1 rounded text-xs ${user.status === "ACTIVE"
                                                                ? "bg-red-100 text-red-700 hover:bg-red-200"
                                                                : "bg-green-100 text-green-700 hover:bg-green-200"
                                                                }`}
                                                        >
                                                            {user.status === "ACTIVE" ? "Deactivate" : "Activate"}
                                                        </button>
                                                        <button
                                                            onClick={() => deleteUser(user.id)}
                                                            className="px-3 py-1 rounded text-xs bg-red-600 text-white hover:bg-red-700"
                                                        >
                                                            Delete
                                                        </button>
                                                        {(!user.firstName || !user.lastName) && (
                                                            <button
                                                                onClick={() => setEditingUser(user)}
                                                                className="px-3 py-1 rounded text-xs bg-yellow-100 text-yellow-700 hover:bg-yellow-200"
                                                            >
                                                                Add Names
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Registration Codes Tab */}
                    {activeTab === "codes" && (
                        <div>
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-semibold">Registration Codes</h3>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => generateCode("STUDENT")}
                                        disabled={loading}
                                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                                    >
                                        Generate Student Code
                                    </button>
                                    <button
                                        onClick={() => generateCode("INSTRUCTOR")}
                                        disabled={loading}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                    >
                                        Generate Instructor Code
                                    </button>
                                </div>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full border-collapse border border-gray-200">
                                    <thead>
                                        <tr className="bg-gray-50">
                                            <th className="border border-gray-200 px-4 py-2 text-left">Code</th>
                                            <th className="border border-gray-200 px-4 py-2 text-left">Role</th>
                                            <th className="border border-gray-200 px-4 py-2 text-left">Status</th>
                                            <th className="border border-gray-200 px-4 py-2 text-left">Expires</th>
                                            <th className="border border-gray-200 px-4 py-2 text-left">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {registrationCodes.map(code => (
                                            <tr key={code.id} className="hover:bg-gray-50">
                                                <td className="border border-gray-200 px-4 py-2 font-mono text-sm">{code.code}</td>
                                                <td className="border border-gray-200 px-4 py-2">
                                                    <span className={`px-2 py-1 rounded-full text-xs ${code.role === "INSTRUCTOR" ? "bg-blue-100 text-blue-800" : "bg-green-100 text-green-800"
                                                        }`}>
                                                        {code.role.toLowerCase()}
                                                    </span>
                                                </td>
                                                <td className="border border-gray-200 px-4 py-2">
                                                    <span className={`px-2 py-1 rounded-full text-xs ${code.used ? "bg-gray-100 text-gray-800" : "bg-green-100 text-green-800"
                                                        }`}>
                                                        {code.used ? "Used" : "Available"}
                                                    </span>
                                                </td>
                                                <td className="border border-gray-200 px-4 py-2">
                                                    {new Date(code.expiresAt).toLocaleDateString()}
                                                </td>
                                                <td className="border border-gray-200 px-4 py-2">
                                                    <button
                                                        onClick={() => deleteCode(code.id)}
                                                        className="px-3 py-1 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200"
                                                    >
                                                        Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                                <h4 className="font-semibold text-blue-800 mb-2">How Registration Codes Work:</h4>
                                <ul className="text-sm text-blue-700 space-y-1">
                                    <li>• Generate unique codes for new students and instructors</li>
                                    <li>• Share these codes with new users for registration</li>
                                    <li>• Codes expire after 1 year and can be deleted if needed</li>
                                    <li>• Used codes are automatically marked as "Used"</li>
                                </ul>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Edit User Names Modal */}
            {editingUser && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold">Add Names for {editingUser.email}</h3>
                            <button
                                onClick={() => setEditingUser(null)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                ×
                            </button>
                        </div>

                        <form onSubmit={(e) => {
                            e.preventDefault();
                            const formData = new FormData(e.target);
                            const firstName = formData.get('firstName');
                            const lastName = formData.get('lastName');
                            updateUserNames(editingUser.id, firstName, lastName);
                        }}>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">First Name</label>
                                    <input
                                        type="text"
                                        name="firstName"
                                        defaultValue={editingUser.firstName || ""}
                                        className="w-full rounded-lg border px-3 py-2"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Last Name</label>
                                    <input
                                        type="text"
                                        name="lastName"
                                        defaultValue={editingUser.lastName || ""}
                                        className="w-full rounded-lg border px-3 py-2"
                                        required
                                    />
                                </div>

                                <div className="flex gap-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => setEditingUser(null)}
                                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                    >
                                        {loading ? "Saving..." : "Save Names"}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
